import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { FILTER_TEMPLATES, FilterBarService } from '@modules/filters/filter-bar.service';
import { FilterButtonsComponent } from '@modules/filters/filter-buttons/filter-buttons.component';
import { PatientActionsService } from '@modules/patient/patient-actions.service';
import { LoaderComponent } from '@modules/shared/loader/loader.component';
import { PAGINATED_LIST_SOURCE } from '@modules/shared/paginated-list/paginated-list.component';
import { TelemonitoringActionsService } from '@modules/telemonitoring/telemonitoring-actions.service';
import { TelemonitoringListComponent } from '@modules/telemonitoring/telemonitoring-list/telemonitoring-list.component';
import { Store } from '@ngrx/store';
import { stringToBoolean } from '@pixacare/shared';
import { TelemonitoringStorePaginationService } from '@services/telemonitoring-store-pagination.service';
import { telemonitoringFilterTemplates } from '@shared/models/filters/filter-template.config';
import { MenuActionId } from '@shared/models/menu-actions/menu-action-id.enum';
import { selectQueryParams } from '@shared/store/router/router.selectors';
import { selectIsTelemonitoringStateLoaded } from '@shared/store/telemonitorings/telemonitorings.selectors';
import { TuiButton } from '@taiga-ui/core';
import { first, map } from 'rxjs';
import { PatientService } from '../patient/patient.service';
import { TuiAnimated } from '@taiga-ui/cdk';

@Component({
  hostDirectives: [TuiAnimated],
  templateUrl: './patient-telemonitorings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    FilterBarService,
    {
      provide: FILTER_TEMPLATES,
      useValue: telemonitoringFilterTemplates,
    },
    {
      provide: PAGINATED_LIST_SOURCE,
      useExisting: TelemonitoringStorePaginationService,
    },
  ],
  imports: [
    TranslocoDirective,
    TelemonitoringListComponent,
    AsyncPipe,
    TuiButton,
    LoaderComponent,
    FilterButtonsComponent,
  ],
  host: { class: 'flex flex-col' },
})
export class PatientTelemonitoringsComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly patientPageService = inject(PatientService);
  private readonly telemonitoringActionsService = inject(TelemonitoringActionsService);
  private readonly patientActionsService = inject(PatientActionsService);
  private readonly filterBarService = inject(FilterBarService);

  readonly actions = this.telemonitoringActionsService.getActions();
  readonly patient$ = this.patientPageService.getPatient();
  readonly isDataLoaded$ = this.store.select(selectIsTelemonitoringStateLoaded);
  readonly isArchived$ = this.store
    .select(selectQueryParams)
    .pipe(map((params) => stringToBoolean(params.is_archived)));

  ngOnInit(): void {
    this.filterBarService.init();
    this.patientPageService.getTelemonitorings();
  }

  createTelemonitoring(): void {
    this.patient$.pipe(first()).subscribe((patient) => {
      this.patientActionsService.getAction(MenuActionId.PATIENT_TELEMONITOR).execute(patient);
    });
  }
}
